import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BUCKET_REST_SEC,
  CALIBRATION_SCRIPTS,
  CalibrationRecorder,
  LIVE_KEY_METRICS,
  analyzeBuckets,
  logStatsTable,
  type CalibrationScriptKey,
} from '@engine/calibration';
import { useHandStore } from '@store/index';
import './CalibrationWizard.css';

type Phase = 'idle' | 'rest' | 'recording' | 'paused' | 'done';
type Resumable = 'rest' | 'recording';

interface Props {
  onClose: () => void;
}

/**
 * Guided calibration timeline. Walks the user through ~51 labeled
 * recording buckets, then dumps a JSON file + per-bucket stats table so
 * thresholds can be auto-chosen from the captured distributions.
 *
 * Controls during a take:
 *   - Pause / Resume : stops the clock AND recorder so the user can
 *                      re-pose; resume continues the SAME bucket with
 *                      the remaining time. Partial captures before the
 *                      pause are preserved.
 *   - Redo           : discards everything captured for the current
 *                      bucket and restarts its rest → record cycle.
 *   - Abort          : stop the whole run.
 */
export function CalibrationWizard({ onClose }: Props) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [bucketIndex, setBucketIndex] = useState(0);
  const [remainingSec, setRemainingSec] = useState(0);
  const [label, setLabel] = useState('');
  const labelRef = useRef('');
  const [scriptKey, setScriptKey] = useState<CalibrationScriptKey>('full');
  const scriptKeyRef = useRef<CalibrationScriptKey>('full');
  const script = useMemo(() => CALIBRATION_SCRIPTS[scriptKey], [scriptKey]);
  const activeScriptRef = useRef(script);
  activeScriptRef.current = script;

  const recorderRef = useRef(new CalibrationRecorder());
  const captureEnabledRef = useRef(false);

  // One phase timer at a time. `deadlineRef` lets the 1 Hz tick compute
  // `remainingSec` without drift (vs. decrementing).
  const phaseTimeoutRef = useRef<number | null>(null);
  const phaseTickRef = useRef<number | null>(null);
  const deadlineRef = useRef<number | null>(null);

  // Pause bookkeeping.
  const pausedPhaseRef = useRef<Resumable | null>(null);
  const pausedRemainingMsRef = useRef(0);

  const frameRafRef = useRef(0);

  const total = script.length;
  const bucket =
    phase !== 'idle' && phase !== 'done' ? script[bucketIndex] : null;

  // rAF capture loop — runs for the whole wizard lifetime. Only appends
  // to the recorder while `captureEnabledRef` is true.
  useEffect(() => {
    const tick = () => {
      if (captureEnabledRef.current) {
        const hands = useHandStore.getState();
        recorderRef.current.observe(
          hands.left.landmarks,
          hands.left.updatedAt,
          hands.right.landmarks,
          hands.right.updatedAt,
        );
      }
      frameRafRef.current = requestAnimationFrame(tick);
    };
    frameRafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRafRef.current);
  }, []);

  const clearPhaseTimers = useCallback(() => {
    if (phaseTimeoutRef.current !== null) {
      clearTimeout(phaseTimeoutRef.current);
      phaseTimeoutRef.current = null;
    }
    if (phaseTickRef.current !== null) {
      clearInterval(phaseTickRef.current);
      phaseTickRef.current = null;
    }
    deadlineRef.current = null;
  }, []);

  /** Schedules a timed phase with countdown + on-complete callback. */
  const schedulePhase = useCallback((durationMs: number, onDone: () => void) => {
    deadlineRef.current = performance.now() + durationMs;
    setRemainingSec(Math.max(0, Math.ceil(durationMs / 1000)));
    phaseTickRef.current = window.setInterval(() => {
      const d = deadlineRef.current;
      if (d === null) return;
      setRemainingSec(Math.max(0, Math.ceil((d - performance.now()) / 1000)));
    }, 200);
    phaseTimeoutRef.current = window.setTimeout(() => {
      clearPhaseTimers();
      onDone();
    }, durationMs);
  }, [clearPhaseTimers]);

  const finish = useCallback(() => {
    clearPhaseTimers();
    captureEnabledRef.current = false;
    setPhase('done');
    const buckets = recorderRef.current.getBuckets();
    const stats = analyzeBuckets(buckets);

    const lbl = labelRef.current.trim();
    const slug = lbl
      ? lbl.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)
      : '';
    const payload = { capturedAt: new Date().toISOString(), label: lbl, buckets, stats };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const suffix = slug ? `${slug}-${Date.now()}` : `${Date.now()}`;
    a.download = `airdantranh-calibration-${suffix}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    // eslint-disable-next-line no-console
    console.groupCollapsed('[calibration] stats (p5 | p50 | p95 per metric)');
    logStatsTable(stats, LIVE_KEY_METRICS);
    // eslint-disable-next-line no-console
    console.log('full stats object:', stats);
    // eslint-disable-next-line no-console
    console.groupEnd();
  }, [clearPhaseTimers]);

  /** Start recording the bucket at `index` (already in rest). */
  const startRecording = useCallback(
    (index: number, durationMs: number) => {
      const b = activeScriptRef.current[index]!;
      recorderRef.current.startBucket(b);
      captureEnabledRef.current = true;
      setPhase('recording');
      schedulePhase(durationMs, () => {
        captureEnabledRef.current = false;
        recorderRef.current.endBucket();
        if (index + 1 >= total) finish();
        else runBucket(index + 1);
      });
    },
    // runBucket reference below; define via ref to break cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schedulePhase, total, finish],
  );

  /** Begin the rest → record cycle for `index`. */
  const runBucket = useCallback(
    (index: number) => {
      if (index >= total) {
        finish();
        return;
      }
      captureEnabledRef.current = false;
      setBucketIndex(index);
      setPhase('rest');
      schedulePhase(BUCKET_REST_SEC * 1000, () => {
        startRecording(index, activeScriptRef.current[index]!.durationSec * 1000);
      });
    },
    [schedulePhase, total, finish, startRecording],
  );

  const start = () => {
    labelRef.current = label;
    scriptKeyRef.current = scriptKey;
    activeScriptRef.current = CALIBRATION_SCRIPTS[scriptKey];
    recorderRef.current = new CalibrationRecorder();
    runBucket(0);
  };

  const abort = () => {
    clearPhaseTimers();
    captureEnabledRef.current = false;
    recorderRef.current.abort();
    pausedPhaseRef.current = null;
    setPhase('idle');
  };

  /** Discard current bucket's partial capture and restart from rest. */
  const redo = () => {
    clearPhaseTimers();
    captureEnabledRef.current = false;
    recorderRef.current.abort();
    pausedPhaseRef.current = null;
    runBucket(bucketIndex);
  };

  const pause = () => {
    if (phase !== 'rest' && phase !== 'recording') return;
    const d = deadlineRef.current;
    if (d === null) return;
    pausedRemainingMsRef.current = Math.max(0, d - performance.now());
    pausedPhaseRef.current = phase;
    clearPhaseTimers();
    captureEnabledRef.current = false;
    setPhase('paused');
  };

  const resume = () => {
    if (phase !== 'paused') return;
    const prev = pausedPhaseRef.current;
    const remainingMs = pausedRemainingMsRef.current;
    if (prev === null) return;
    pausedPhaseRef.current = null;

    if (prev === 'recording') {
      captureEnabledRef.current = true;
      setPhase('recording');
      schedulePhase(remainingMs, () => {
        captureEnabledRef.current = false;
        recorderRef.current.endBucket();
        if (bucketIndex + 1 >= total) finish();
        else runBucket(bucketIndex + 1);
      });
    } else {
      // Was resting — finish the rest, then roll into a fresh recording.
      setPhase('rest');
      schedulePhase(remainingMs, () => {
        startRecording(bucketIndex, activeScriptRef.current[bucketIndex]!.durationSec * 1000);
      });
    }
  };

  useEffect(() => () => clearPhaseTimers(), [clearPhaseTimers]);

  const totalDurationSec = script.reduce(
    (acc, b) => acc + b.durationSec + BUCKET_REST_SEC,
    0,
  );

  const phaseClass =
    phase === 'rest'
      ? ' calib-wizard--rest'
      : phase === 'recording'
        ? ' calib-wizard--recording'
        : phase === 'paused'
          ? ' calib-wizard--paused'
          : '';

  return (
    <div className={`calib-wizard${phaseClass}`}>
      <div className="calib-wizard__header">
        <span className="calib-wizard__progress">
          {phase === 'idle' || phase === 'done'
            ? `${total} buckets · ~${totalDurationSec}s total`
            : `Bucket ${bucketIndex + 1} / ${total}`}
        </span>
      </div>

      <div className="calib-wizard__main">
        {phase === 'idle' && (
          <>
            <div className="calib-wizard__state">Calibration</div>
            <div className="calib-wizard__prompt">
              Follow the prompts. Each bucket = 2s rest (read + reposition)
              + 3s recording. You can Pause, Redo, or Abort at any time. At
              the end a JSON file downloads and a stats table prints to the
              browser console.
            </div>
            <label className="calib-wizard__label">
              <span className="calib-wizard__label-k">Script</span>
              <select
                className="calib-wizard__label-input"
                value={scriptKey}
                onChange={(e) => setScriptKey(e.target.value as CalibrationScriptKey)}
              >
                <option value="full">
                  Full ({CALIBRATION_SCRIPTS.full.length} buckets)
                </option>
                <option value="thumb_dynamics">
                  Thumb gliss dynamics only ({CALIBRATION_SCRIPTS.thumb_dynamics.length} buckets — nhẹ / vừa / to, L&amp;R)
                </option>
              </select>
            </label>
            <label className="calib-wizard__label">
              <span className="calib-wizard__label-k">Session label</span>
              <input
                type="text"
                className="calib-wizard__label-input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. run-2, after-lunch, v2"
                maxLength={40}
              />
              <span className="calib-wizard__label-hint">
                Appears in the filename so two runs don't collide when comparing.
              </span>
            </label>
          </>
        )}
        {phase === 'rest' && bucket && (
          <>
            <div className="calib-wizard__state">Next up — get ready</div>
            <div className="calib-wizard__prompt">{bucket.prompt}</div>
            <div className="calib-wizard__reps">{bucket.reps}</div>
            <div className="calib-wizard__countdown">{remainingSec}</div>
          </>
        )}
        {phase === 'recording' && bucket && (
          <>
            <div className="calib-wizard__state">● Recording</div>
            <div className="calib-wizard__prompt">{bucket.prompt}</div>
            <div className="calib-wizard__reps">{bucket.reps}</div>
            <div className="calib-wizard__countdown">{remainingSec}</div>
            <div className="calib-wizard__bar">
              <div
                className="calib-wizard__bar-fill"
                style={{
                  width: `${Math.round(
                    (1 - remainingSec / bucket.durationSec) * 100,
                  )}%`,
                }}
              />
            </div>
          </>
        )}
        {phase === 'paused' && bucket && (
          <>
            <div className="calib-wizard__state">Paused</div>
            <div className="calib-wizard__prompt">{bucket.prompt}</div>
            <div className="calib-wizard__reps">
              Reset your hand, then Resume to continue this bucket.
            </div>
          </>
        )}
        {phase === 'done' && (
          <>
            <div className="calib-wizard__state">Done</div>
            <div className="calib-wizard__prompt">
              JSON downloaded. Stats table printed to the console. Share the
              JSON with me and I'll pick thresholds.
            </div>
          </>
        )}
      </div>

      <div className="calib-wizard__footer">
        {phase === 'idle' && (
          <>
            <button type="button" className="calib-wizard__btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="calib-wizard__btn calib-wizard__btn--primary"
              onClick={start}
            >
              Start
            </button>
          </>
        )}
        {(phase === 'rest' || phase === 'recording') && (
          <>
            <button type="button" className="calib-wizard__btn" onClick={abort}>
              Abort
            </button>
            <button type="button" className="calib-wizard__btn" onClick={redo}>
              Redo this
            </button>
            <button type="button" className="calib-wizard__btn" onClick={pause}>
              Pause
            </button>
          </>
        )}
        {phase === 'paused' && (
          <>
            <button type="button" className="calib-wizard__btn" onClick={abort}>
              Abort
            </button>
            <button type="button" className="calib-wizard__btn" onClick={redo}>
              Redo this
            </button>
            <button
              type="button"
              className="calib-wizard__btn calib-wizard__btn--primary"
              onClick={resume}
            >
              Resume
            </button>
          </>
        )}
        {phase === 'done' && (
          <button
            type="button"
            className="calib-wizard__btn calib-wizard__btn--primary"
            onClick={onClose}
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
}

/** Floating trigger button — shown on the stage next to the gear icon. */
export function CalibrationTrigger({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      className="calib-trigger"
      onClick={onOpen}
      aria-label="Open calibration wizard"
    >
      CALIB
    </button>
  );
}
